import { Link, matchPath, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useBreadcrumbsOverride } from "./BreadcrumbsContext";

const LABELS: Record<string, string> = {
  "": "Dashboard",
  admin: "Admin",
  users: "Users",
  roles: "Roles & Permissions",
  audit: "Audit Log",
  prompts: "Prompt Templates",
  forms: "Form Templates",
  documents: "Documents",
  reports: "Reports",
  brokers: "Brokers",
  pipeline: "Deal Pipeline",
  "deal-flow": "Deals",
  metrics: "Metrics",
  "failure-tracking": "Failure Tracking",
  escalations: "Escalations",
  loans: "Loans",
  vendors: "Vendors",
  complaints: "Complaints",
  "vendor-audits": "Annual Audit",
  "vfi-accounting": "VFI Accounting",
  defaults: "Defaults",
  "portfolio-admin": "Portfolio Administration",
  "local-enforcers": "Local Enforcers",
  waterfalls: "Waterfalls",
  "loan-tape": "Loan Tape (Monthly Close)",
  "loan-payments": "Loan Payments",
  spv: "SPV Pools",
  investors: "Investor Reporting",
  compliance: "Compliance",
  tasks: "Tasks",
  collection: "External Collection",
  packages: "Document Requests",
  staging: "Review Queue",
  "portal-accounts": "Portal Accounts",
  "collection-templates": "Collection Templates",
  "collection-bundles": "Bundles",
  "deal-submission-templates": "Deal Templates",
  "portal-materials": "Portal Materials",
  // Buyer CRM (PR 2a) / Buyer programme (PR 2). The earlier note here claimed
  // "buyers" and "listings" "already read correctly" and left them out. They
  // did not: only the LEAF crumb carries the `uppercase` transform (see the
  // render below), so an INTERMEDIATE segment renders its raw slug — a buyer's
  // detail page read "Dashboard › buyers › …", lower-case, next to a set of
  // properly-cased siblings.
  buyers: "Buyers",
  listings: "Listings",
  "buyer-submissions": "Submissions",
  "buyer-assignments": "Assignment Queue",
  // Multi-manager buyer teams (WS4). "teams" is an intermediate segment on
  // /buyers/teams/:id — and intermediate crumbs carry no `uppercase` transform
  // (see the render below) — so without an entry it renders as a lower-case
  // slug beside title-cased siblings. It serves /brokers/teams too, which had
  // the same defect.
  teams: "Teams",
  // Same class as buyers/listings above: "prequalification" is only ever an
  // INTERMEDIATE segment (/deal-flow/prequalification/{documents,brochure,:id}),
  // and an intermediate crumb carries no `uppercase` transform — so without an
  // entry it rendered as a lower-case slug beside title-cased siblings on all
  // three of those pages.
  prequalification: "Prequalification",
  brochure: "Program Brochure",
};

// A path segment that is a record id rather than a word.
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "Report Templates" → "Report Template"; "Entities" → "Entity". */
function singularize(label: string): string {
  if (/ies$/i.test(label)) return label.replace(/ies$/i, "y");
  // "Businesses" → "Business". Checked before the bare trailing "s" so the
  // "es" is removed whole rather than leaving "Businesse".
  if (/(s|x|z|ch|sh)es$/i.test(label)) return label.replace(/es$/i, "");
  // Already singular and ending in "ss" ("Business") — leave it alone.
  if (/ss$/i.test(label)) return label;
  if (/s$/i.test(label)) return label.replace(/s$/i, "");
  return label;
}

/** "report-templates" → "Report Templates". Only used to derive a noun for a
 *  GUID crumb; ordinary segments keep their raw slug (see `segmentLabel`). */
function prettifySegment(segment: string): string {
  return decodeURIComponent(segment)
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * The noun a GUID crumb reads as, taken from the collection it sits under:
 * `/buyers/<guid>` → "Buyer", `/portfolio-admin/loans/<guid>` → "Loan".
 *
 * A raw GUID in the trail is worse than useless — it is 36 characters of noise
 * where the reader expects to be told what they are looking at, and on a detail
 * page it is the LEAF, i.e. the most prominent crumb. Pages that know the
 * record's own name already override the whole trail via `useBreadcrumbs`;
 * this is the honest fallback for the ones that don't, and it never invents a
 * name it does not have.
 */
function recordNounFor(parentSegment: string | undefined): string {
  if (!parentSegment || UUID_SEGMENT.test(parentSegment)) return "Record";
  return singularize(LABELS[parentSegment] ?? prettifySegment(parentSegment));
}

/**
 * The crumb text for one URL segment. Ordinary segments are unchanged —
 * a known label, else the raw (decoded) slug — so only GUIDs take the new path.
 */
function segmentLabel(segment: string, parentSegment: string | undefined): string {
  const known = LABELS[segment];
  if (known) return known;
  if (UUID_SEGMENT.test(segment)) return recordNounFor(parentSegment);
  return decodeURIComponent(segment);
}

// Mirror of the path patterns registered under <Route> in App.tsx. Used to
// gate breadcrumb intermediate-segment links so we never render a <Link>
// pointing at a path that has no route — which would land the user on the
// catch-all NOT FOUND page (the staging /profile and /settings dead-link
// reports). Keep this in sync with App.tsx; adding a new top-level route
// requires adding it here so its breadcrumb segment becomes clickable.
const ROUTE_PATTERNS: readonly string[] = [
  "/",
  "/login",
  "/account",
  "/account/password",
  "/account/profile",
  "/account/profile/edit",
  "/account/profile/:userId",
  "/settings/notifications",
  "/documents",
  "/admin",
  "/admin/folder-templates",
  "/admin/brand-identity",
  "/admin/users",
  "/admin/qualification-fields",
  "/admin/roles",
  "/admin/roles-permissions",
  "/admin/categories",
  "/admin/audit",
  "/admin/report-templates",
  "/admin/report-templates/new",
  "/admin/report-templates/:id",
  "/admin/territories",
  "/admin/commission-pools",
  "/admin/commission-periods",
  "/admin/collection-templates",
  "/admin/collection-bundles",
  "/admin/deal-submission-templates",
  "/admin/portal-materials",
  "/reports",
  "/reports/generate",
  "/reports/:id",
  "/reports/:id/legacy",
  "/brokers",
  "/brokers/new",
  "/brokers/:id",
  "/brokers/:id/edit",
  "/leads",
  "/leads/import",
  "/leads/:id",
  // Buyer CRM (PR 2a). The four static/nested buyer routes below were
  // registered in App.tsx but never mirrored here, so their parent "Buyers"
  // crumb rendered as dead trail text instead of a link back to the list.
  "/buyers",
  "/buyers/new",
  "/buyers/import",
  "/buyers/allocation",
  "/buyers/:id",
  "/buyers/:id/edit",
  "/listings",
  "/listings/:id",
  "/buyer-submissions",
  "/buyer-submissions/:id",
  "/buyer-assignments",
  "/pipeline",
  "/deal-flow/pipeline",
  "/deal-flow/pipeline/archive",
  "/deal-flow/deals/:dealId",
  "/deal-flow/deals/:dealId/edit",
  "/deal-flow/metrics",
  "/deal-flow/failure-tracking",
  "/portfolio-admin/loans",
  "/portfolio-admin/loans/:loanId",
  "/portfolio-admin/vendors",
  "/portfolio-admin/vendors/:vendorId",
  "/portfolio-admin/complaints",
  "/portfolio-admin/vendor-audits",
  "/portfolio-admin/vfi-accounting",
  "/portfolio-admin/local-enforcers",
  "/portfolio-admin/local-enforcers/:enforcerId",
  "/portfolio-admin/waterfalls",
  "/portfolio-admin/loan-tape",
  "/portfolio-admin/metrics",
  "/spv",
  "/investors",
  "/compliance",
  "/commissions",
  "/commissions/new",
  "/commissions/:id",
  "/commissions/:id/edit",
  "/commissions/bulk",
  "/commissions/templates",
  "/messages",
  "/messages/new",
  "/messages/:id",
  "/tasks",
  "/tasks/:id",
  "/admin/tasks/tags",
];

export function isRegisteredRoute(path: string): boolean {
  return ROUTE_PATTERNS.some((pattern) => matchPath({ path: pattern, end: true }, path) !== null);
}

// Path stems that have no route of their own but should still link to a
// canonical landing page. The "Deals" crumb (URL stem /deal-flow) points
// at the pipeline so every deal-flow page can navigate back from it.
const SEGMENT_HREF_OVERRIDE: Record<string, string> = {
  "deal-flow": "/deal-flow/pipeline",
};

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const override = useBreadcrumbsOverride();

  if (override && override.length > 0) {
    return (
      <nav aria-label="Breadcrumb" className="text-caption text-gray-mid flex items-center gap-space-1">
        <Link to="/" className="inline-flex items-center gap-space-1 hover:text-onyx">
          <Home size={14} strokeWidth={1.5} aria-hidden /> Dashboard
        </Link>
        {override.map((c, idx) => {
          const isLast = idx === override.length - 1;
          return (
            <span key={`${c.label}-${idx}`} className="inline-flex items-center gap-space-1">
              <ChevronRight size={14} strokeWidth={1.5} aria-hidden />
              {isLast ? (
                <span className="font-display font-semibold uppercase tracking-wide text-onyx">{c.label}</span>
              ) : c.href ? (
                <Link to={c.href} className="hover:text-onyx">{c.label}</Link>
              ) : (
                <span className="text-gray-mid">{c.label}</span>
              )}
            </span>
          );
        })}
      </nav>
    );
  }

  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return (
      <nav aria-label="Breadcrumb" className="text-caption text-gray-mid flex items-center gap-space-1">
        <Home size={14} strokeWidth={1.5} aria-hidden />
        <span className="font-display font-semibold uppercase tracking-wide text-onyx">Dashboard</span>
      </nav>
    );
  }

  return (
    <nav aria-label="Breadcrumb" className="text-caption text-gray-mid flex items-center gap-space-1">
      <Link to="/" className="inline-flex items-center gap-space-1 hover:text-onyx">
        <Home size={14} strokeWidth={1.5} aria-hidden /> Dashboard
      </Link>
      {segments.map((s, idx) => {
        const href = "/" + segments.slice(0, idx + 1).join("/");
        const label = segmentLabel(s, idx > 0 ? segments[idx - 1] : undefined);
        const isLast = idx === segments.length - 1;
        // Intermediate segments are linked only when the cumulative path
        // is itself a registered route. When it isn't (e.g. /settings is
        // a path stem with no route of its own — only /settings/notifications
        // is registered), we render plain trail text instead so the user
        // can't click into the NOT FOUND page. A handful of stems route to
        // a canonical landing page instead (SEGMENT_HREF_OVERRIDE).
        const linkHref = SEGMENT_HREF_OVERRIDE[s] ?? href;
        const linkable = !isLast && (isRegisteredRoute(href) || s in SEGMENT_HREF_OVERRIDE);
        return (
          <span key={href} className="inline-flex items-center gap-space-1">
            <ChevronRight size={14} strokeWidth={1.5} aria-hidden />
            {isLast ? (
              <span className="font-display font-semibold uppercase tracking-wide text-onyx">{label}</span>
            ) : linkable ? (
              <Link to={linkHref} className="hover:text-onyx">{label}</Link>
            ) : (
              <span className="text-gray-mid">{label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
